import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import plugins from 'suneditor/src/plugins';
import { Button, Form, Row, Col, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { blogCategoryService } from '../../../api/blog/blogCategoryService';
import { addBlog, BlogAddPayload, FAQItem, APIFAQItem } from '../../../api/blog/addBlog';
import imageCompression from 'browser-image-compression';
import secureApi from '../../../utils/secureApi';
import { blogService } from '../../../api/blog/blogService';
import { getImageUrl } from '../../../utils/imageUrl';

const initialContent = { title: '', description: '' };
const initialFAQ = { question: '', answer: '' };

const BlogDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const blog = location.state?.blog;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [image, setImage] = useState<string | null>(null); // S3 key
  const [imagePreview, setImagePreview] = useState<string | null>(null); // For preview
  const [isActive, setIsActive] = useState(true);
  const [isFeatureBlog, setIsFeatureBlog] = useState(false);
  const [isBannerBlog, setIsBannerBlog] = useState(false);
  const [isMustReadBlog, setIsMustReadBlog] = useState(false);
  const [sortNumber, setSortNumber] = useState(1);
  const [category, setCategory] = useState<{ value: string; label: string } | null>(null);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [contents, setContents] = useState([{ ...initialContent }]);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [editorUploading, setEditorUploading] = useState(false);
  const [editorUploadError, setEditorUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const initialBlogRef = useRef<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isUploadingEditorImages, setIsUploadingEditorImages] = useState(false);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  // Fix category dropdown logic
  useEffect(() => {
    blogCategoryService.getAllCategories().then((res: any) => {
      console.log('Category API response:', res);
      let catList = [];
      if (Array.isArray(res)) {
        catList = res;
      } else if (Array.isArray(res.data?.paginatedResults)) {
        catList = res.data.paginatedResults;
      } else if (Array.isArray(res.data)) {
        catList = res.data;
      } else if (Array.isArray(res.data?.data)) {
        catList = res.data.data;
      }
      setCategories(
        catList.map((cat: any) => ({ value: cat._id, label: cat.category || cat.name }))
      );
    });
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // When loading a blog for edit, store the initial data
  useEffect(() => {
    async function fetchAndPrefill(blogData: any) {
      initialBlogRef.current = blogData;
      setTitle(blogData.title || '');
      setDescription(blogData.description || '');
      setPublishDate(blogData.publishedDate ? blogData.publishedDate.slice(0, 10) : '');
      setImage(blogData.image || '');
      setImagePreview(blogData.image ? getImageUrl(blogData.image) : '');
      setIsActive(blogData.isActive ?? true);
      setIsFeatureBlog(blogData.isFeatureBlog ?? false);
      setIsBannerBlog(blogData.isBannerBlog ?? false);
      setIsMustReadBlog(blogData.isMustReadBlog ?? false);
      setSortNumber(blogData.sortNumber ?? 1);
      setCategory(
        blogData.blogCategory
          ? typeof blogData.blogCategory === 'object'
            ? { value: blogData.blogCategory._id, label: blogData.blogCategory.category }
            : { value: blogData.blogCategory, label: blogData.blogCategory }
          : null
      );
      setContents(blogData.contents || []);
      // Handle FAQ data properly - transform from API format to UI format
      // API returns "faq" with { title, description }, UI expects "faqs" with { question, answer }
      if (blogData.faq && Array.isArray(blogData.faq) && blogData.faq.length > 0) {
        // Transform API format to UI format
        const uiFAQs: FAQItem[] = blogData.faq.map((apiFaq: any) => ({
          question: apiFaq.title || '',
          answer: apiFaq.description || ''
        }));
        setFaqs(uiFAQs);
      } else if (blogData.faqs && Array.isArray(blogData.faqs) && blogData.faqs.length > 0) {
        // Fallback for old format (if API still returns "faqs" with { question, answer })
        setFaqs(blogData.faqs);
      } else {
        setFaqs([]);
      }
      setMetaTitle(blogData.metaTitle || '');
      setMetaDescription(blogData.metaDescription || '');
    }

    if (blog) {
      fetchAndPrefill(blog);
    } else if (id) {
      // If no state, fetch from API
      blogService.getBlogDetails(id).then((res: any) => {
        let blogData = res;
        if (res.data) blogData = res.data;
        fetchAndPrefill(blogData);
      });
    }
  }, [blog, id]);

  // Robust deepEqual function for dirty-checking
  function deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
    if (typeof a !== typeof b) return false;
    if (typeof a !== 'object' || a === null || b === null) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
      if (!deepEqual(a[key], b[key])) return false;
    }
    return true;
  }

  // Compare form state to initialBlogRef for hasChanges
  useEffect(() => {
    if (!id || !initialBlogRef.current) {
      setHasChanges(true);
      return;
    }
    const initial = initialBlogRef.current;
    const changed =
      title !== (initial.title || '') ||
      description !== (initial.description || '') ||
      publishDate !== (initial.publishedDate ? initial.publishedDate.slice(0, 10) : '') ||
      image !== (initial.image || '') ||
      isActive !== (initial.isActive ?? true) ||
      isFeatureBlog !== (initial.isFeatureBlog ?? false) ||
      isBannerBlog !== (initial.isBannerBlog ?? false) ||
      isMustReadBlog !== (initial.isMustReadBlog ?? false) ||
      sortNumber !== (initial.sortNumber ?? 1) ||
      (category?.value !== (typeof initial.blogCategory === 'object' ? initial.blogCategory._id : initial.blogCategory)) ||
      !deepEqual(contents, initial.contents || []) ||
      !deepEqual(faqs, (() => {
        // Handle both API formats for change detection
        if (initial.faq && Array.isArray(initial.faq)) {
          return initial.faq.map((apiFaq: any) => ({
            question: apiFaq.title || '',
            answer: apiFaq.description || ''
          }));
        } else if (initial.faqs && Array.isArray(initial.faqs)) {
          return initial.faqs;
        }
        return [];
      })()) ||
      metaTitle !== (initial.metaTitle || '') ||
      metaDescription !== (initial.metaDescription || '');
    console.log('hasChanges?', changed, {
      title, initialTitle: initial.title,
      description, initialDescription: initial.description,
      publishDate, initialPublishDate: initial.publishedDate,
      image, initialImage: initial.image,
      isActive, initialIsActive: initial.isActive,
      isFeatureBlog, initialIsFeatureBlog: initial.isFeatureBlog,
      isBannerBlog, initialIsBannerBlog: initial.isBannerBlog,
      isMustReadBlog, initialIsMustReadBlog: initial.isMustReadBlog,
      sortNumber, initialSortNumber: initial.sortNumber,
      category, initialCategory: initial.blogCategory,
      contents, initialContents: initial.contents,
      faqs, initialFaqs: initial.faq || initial.faqs,
      metaTitle, initialMetaTitle: initial.metaTitle,
      metaDescription, initialMetaDescription: initial.metaDescription
    });
    setHasChanges(changed);
  }, [id, title, description, publishDate, image, isActive, isFeatureBlog, isBannerBlog, isMustReadBlog, sortNumber, category, contents, faqs, metaTitle, metaDescription]);

  // Helper to generate file path
  const generateFilePath = (title: string, date: Date, ext: string) => {
    const sanitizedTitle = (title || 'untitled').replace(/[^a-zA-Z0-9-_]/g, '_');
    return `${sanitizedTitle}_${Date.now()}.${ext}`;
  };
  // Helper to generate context
  const generateContext = (date: Date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `blog-images/${dd}-${mm}-${yy}`;
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploadError(null);
    setIsImageUploading(true);
    try {
      // BannerType Web compression: quality 90%, no size restriction
      const options = { initialQuality: 0.9, maxWidthOrHeight: 1600, useWebWorker: true };
      let processedFile = file;
      processedFile = await imageCompression(file, options);
      const dateObj = publishDate ? new Date(publishDate) : new Date();
      const ext = processedFile.type.split('/')[1] || 'jpg';
      const fileName = generateFilePath(title, dateObj, ext);
      const context = generateContext(dateObj);
      // Request presigned URL
      const response = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: processedFile.type,
          context
        }
      );
      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;
      if (!presignedUrl) {
        setImageUploadError('Failed to get presigned URL from server.');
        setIsImageUploading(false);
        return;
      }
      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': processedFile.type },
        body: processedFile
      });
      if (uploadResponse.ok) {
        setImage(s3Key);
        setImagePreview(getImageUrl(s3Key));
        setImageUploadError(null);
      } else {
        setImageUploadError('Image upload to S3 failed. Please try again.');
        setImage(null);
        setImagePreview(null);
      }
    } catch (err) {
      setImageUploadError('Image upload failed. Please try again.');
      setImage(null);
      setImagePreview(null);
    } finally {
      setIsImageUploading(false);
    }
  };

  // Helper for SunEditor image upload
  const handleEditorImageUpload = async (file: File, callback: (url: string) => void) => {
    setEditorUploading(true);
    setEditorUploadError(null);
    try {
      // Compress image
      const options = { maxSizeMB: 0.15, maxWidthOrHeight: 1600, initialQuality: 0.92, useWebWorker: true };
      let processedFile = file;
      if (file.size / 1024 > 120) {
        processedFile = await imageCompression(file, options);
      }
      const dateObj = publishDate ? new Date(publishDate) : new Date();
      const ext = processedFile.type.split('/')[1] || 'jpg';
      const fileName = generateFilePath(title, dateObj, ext);
      const context = generateContext(dateObj);
      // Request presigned URL
      const response = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: processedFile.type,
          context
        }
      );
      const presignedUrl = response.data?.presignedUrl;
      const s3Key = response.data?.key;
      if (!presignedUrl) {
        setEditorUploadError('Failed to get presigned URL from server.');
        setEditorUploading(false);
        return;
      }
      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': processedFile.type },
        body: processedFile
      });
      if (uploadResponse.ok) {
        // Insert the full S3 URL into the editor
        const imageUrl = getImageUrl(s3Key);
        callback(imageUrl);
        setEditorUploadError(null);
      } else {
        setEditorUploadError('Image upload to S3 failed. Please try again.');
      }
    } catch (err) {
      setEditorUploadError('Image upload failed. Please try again.');
    } finally {
      setEditorUploading(false);
    }
  };

  const handlePublishNow = () => {
    const now = new Date();
    setPublishDate(now.toISOString().slice(0, 10));
  };

  // Content state and handlers
  const handleContentChange = (idx: number, field: string, value: string) => {
    setContents(contents => contents.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };
  
  // Check if a specific section is complete (has both title and description)
  const isSectionComplete = (section: { title: string; description: string }) => {
    return section.title.trim() !== '' && section.description.trim() !== '';
  };
  
  // Check if we can add a new section (all existing sections must be complete)
  const canAddNewSection = () => {
    return contents.every(section => isSectionComplete(section));
  };
  
  const handleAddContent = () => {
    if (canAddNewSection()) {
      setContents([...contents, { ...initialContent }]);
    }
  };
  
  const handleRemoveContent = (idx: number) => setContents(contents.filter((_, i) => i !== idx));

  // FAQ state and handlers
  const handleFAQChange = (idx: number, field: keyof FAQItem, value: string) => {
    setFaqs(faqs => faqs.map((f, i) => i === idx ? { ...f, [field]: value } : f));
  };
  
  // Check if a specific FAQ is complete (has both question and answer)
  const isFAQComplete = (faq: FAQItem) => {
    return faq.question.trim() !== '' && stripHtmlTags(faq.answer).trim() !== '';
  };

  // Check if we can add a new FAQ (all existing FAQs must be complete)
  const canAddNewFAQ = () => {
    // If no FAQs exist, we can always add one
    if (faqs.length === 0) return true;
    // If FAQs exist, all must be complete
    return faqs.every(faq => isFAQComplete(faq));
  };
  
  const handleAddFAQ = () => {
    if (canAddNewFAQ()) {
      setFaqs([...faqs, { ...initialFAQ }]);
    }
  };
  
  const handleRemoveFAQ = (idx: number) => setFaqs(faqs.filter((_, i) => i !== idx));

  // Utility to remove base64 images from HTML
  const removeBase64Images = (html: string) => {
    return html.replace(/<img[^>]+src=["']data:image\/(png|jpeg|jpg);base64,[^"']+["'][^>]*>/gi, '');
  };

  // Utility: Extract all base64 <img> tags from HTML
  const extractBase64Images = (html: string) => {
    const regex = /<img[^>]+src=["'](data:image\/(png|jpeg|jpg);base64,[^"']+)["'][^>]*>/gi;
    let match;
    const images = [];
    while ((match = regex.exec(html)) !== null) {
      images.push({ base64: match[1], tag: match[0] });
    }
    return images;
  };

  // Utility: Convert base64 to Blob
  const base64ToBlob = (base64: string, contentType: string = '') => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: contentType || base64.split(';')[0].split(':')[1] });
  };

  // Utility: Upload base64 image to S3 and return S3 URL
  const uploadBase64ToS3 = async (base64: string, sectionTitle: string) => {
    // Convert to Blob
    const contentType = base64.match(/^data:(image\/[^;]+);/)?.[1] || '';
    const blob = base64ToBlob(base64, contentType);
    // Generate file name and context
    const dateObj = publishDate ? new Date(publishDate) : new Date();
    const ext = contentType.split('/')[1] || 'jpg';
    const fileName = generateFilePath(sectionTitle, dateObj, ext);
    const context = generateContext(dateObj);
    // Get presigned URL
    const response = await secureApi.post(
      `/api/v1/general/presigned-url`,
      { fileName, contentType, context }
    );
    const presignedUrl = response.data?.presignedUrl;
    const s3Key = response.data?.key;
    if (!presignedUrl) throw new Error('Failed to get presigned URL');
    // Upload
    await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob
    });
    return getImageUrl(s3Key);
  };

  // Helper to strip HTML tags for validation
  const stripHtmlTags = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Helper to check if form is valid
  const isFormValid = () => {
    if (!title.trim() || !stripHtmlTags(description).trim() || !category || !category.value) return false;
    if (!contents.length) return false;
    for (const content of contents) {
      if (!content.title.trim() || !stripHtmlTags(content.description).trim()) return false;
    }
    // Check FAQs - at least one FAQ must be complete if FAQs exist
    if (faqs.length > 0) {
      for (const faq of faqs) {
        if (!faq.question.trim() || !stripHtmlTags(faq.answer).trim()) return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    setIsUploadingEditorImages(true);
    try {
      // Process all content sections for base64 images
      const newContents = await Promise.all(contents.map(async (c) => {
        let html = c.description;
        const images = extractBase64Images(html);
        for (const img of images) {
          const s3Url = await uploadBase64ToS3(img.base64, c.title);
          html = html.replace(img.base64, s3Url);
        }
        return { ...c, description: html };
      }));

      // Process all FAQ sections for base64 images
      const newFAQs = await Promise.all(faqs.map(async (f) => {
        let html = f.answer;
        const images = extractBase64Images(html);
        for (const img of images) {
          const s3Url = await uploadBase64ToS3(img.base64, f.question);
          html = html.replace(img.base64, s3Url);
        }
        return { ...f, answer: html };
      }));

      setIsUploadingEditorImages(false);
      
      // Transform FAQ data to match API structure
      const apiFAQs: APIFAQItem[] = newFAQs.map(faq => ({
        title: faq.question,
        description: faq.answer
      }));
      
      const payload: BlogAddPayload = {
        title,
        description,
        image: image || '', // send only the S3 key, not the full URL
        isActive,
        isFeatureBlog,
        blogCategory: category?.value || '',
        publishedDate: publishDate ? new Date(publishDate).toISOString() : '',
        sortNumber,
        isBannerBlog,
        isMustReadBlog,
        contents: newContents,
        faq: apiFAQs.length > 0 ? apiFAQs : undefined, // API expects "faq" not "faqs"
        tag: "SFL BLOGS", // Add default tag as shown in sample
        html: "", // Add empty html field as shown in sample
        metaTitle: metaTitle || undefined, // SEO meta title
        metaDescription: metaDescription || undefined // SEO meta description
      };
      if (id) {
        await blogService.updateBlog(id, payload);
      } else {
        await addBlog(payload);
      }
      navigate('/blog-details/blogs');
    } catch (err) {
      setIsUploadingEditorImages(false);
      if (err && typeof err === 'object' && 'errors' in err) {
        setFieldErrors((err as any).errors);
      } else {
        setError(typeof err === 'string' ? err : 'Failed to add/edit blog.');
      }
    } finally {
      setSaving(false);
    }
  };

  // SunEditor options for content descriptions
  const getEditorOptions = () => ({
    buttonList: [
      ['undo', 'redo'],
      ['font', 'fontSize', 'formatBlock'],
      ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
      ['removeFormat'],
      ['fontColor', 'hiliteColor'],
      ['indent', 'outdent'],
      ['align', 'horizontalRule', 'list', 'table'],
      ['link', 'image'],
      ['fullScreen', 'showBlocks', 'codeView'],
    ],
    plugins: plugins,
    height: '180px',
    imageUploadSizeLimit: 500 * 1024, // 500KB
    imageUploadHandler: handleEditorImageUpload,
    imageUploadUrl: '', // Not used, we use handler
    imageMultipleFile: false,
    imageAccept: 'image/*',
    imageGalleryUrl: '',
    imageFileInput: true,
    imageUploadHeader: {},
    imageUploadFormat: ['jpg', 'jpeg', 'png'],
    imageUploadInput: true,
    imageUpload: true,
    imageUploadRemoteUrls: false,
    imageUploadRemoteUrl: '',
    imageUploadRemoteUrlHeader: {},
    imageUploadRemoteUrlFormat: [],
    imageUploadRemoteUrlInput: false,
    imageUploadRemoteUrlMultiple: false,
    imageUploadRemoteUrlAccept: '',
    imageUploadRemoteUrlMaxSize: 0,
    imageUploadRemoteUrlMaxFiles: 0,
    imageUploadRemoteUrlMaxTotalSize: 0,
    imageUploadRemoteUrlMaxTotalFiles: 0,
    imageUploadRemoteUrlMaxTotalSizePerFile: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFile: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileType: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileType: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFileType: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFileType: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFileTypePerFileType: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFileTypePerFileType: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFileTypePerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFileTypePerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFileTypePerFileTypePerFileType: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFileTypePerFileTypePerFileType: 0,
    imageUploadRemoteUrlMaxTotalSizePerFileTypePerFileTypePerFileTypePerFileTypePerFile: 0,
    imageUploadRemoteUrlMaxTotalFilesPerFileTypePerFileTypePerFileTypePerFileTypePerFile: 0,
    // Custom onPaste handler to filter out base64 images
    onPaste: (e: any, cleanData: any, maxCharCount: any, core: any) => {
      if (cleanData && typeof cleanData === 'string') {
        // Remove base64 images
        return cleanData.replace(/<img[^>]+src=["']data:image\/(png|jpeg|jpg);base64,[^"']+["'][^>]*>/gi, '');
      }
      return cleanData;
    },
  });

  const handleSectionDescriptionChange = async (idx: number, value: string) => {
    // Check for base64 images
    const images = extractBase64Images(value);
    let html = value;
    if (images.length > 0) {
      setEditorUploading(true);
      for (const img of images) {
        const s3Url = await uploadBase64ToS3(img.base64, contents[idx].title);
        html = html.replace(img.base64, s3Url);
      }
      setEditorUploading(false);
    }
    handleContentChange(idx, 'description', html);
  };

  // FAQ image upload handler
  const handleFAQImageUpload = async (idx: number, html: string) => {
    setEditorUploading(true);
    setEditorUploadError(null);
    
    try {
      // Extract base64 images from HTML
      const base64Images = html.match(/<img[^>]+src=["']data:image\/(png|jpeg|jpg);base64,[^"']+["'][^>]*>/gi);
      
      if (base64Images) {
        for (const img of base64Images) {
          const base64Match = img.match(/src=["']data:image\/(png|jpeg|jpg);base64,([^"']+)["']/);
          if (base64Match) {
            const base64 = base64Match[0];
            const s3Url = await uploadBase64ToS3(base64, faqs[idx].question);
            html = html.replace(base64, s3Url);
          }
        }
      }
      setEditorUploading(false);
    } catch (error) {
      console.error('FAQ image upload error:', error);
      setEditorUploadError('Failed to upload FAQ image');
      setEditorUploading(false);
    }
    handleFAQChange(idx, 'answer', html);
  };

  // Description image upload handler
  const handleDescriptionImageUpload = async (html: string) => {
    setEditorUploading(true);
    setEditorUploadError(null);
    
    try {
      // Extract base64 images from HTML
      const base64Images = html.match(/<img[^>]+src=["']data:image\/(png|jpeg|jpg);base64,[^"']+["'][^>]*>/gi);
      
      if (base64Images) {
        for (const img of base64Images) {
          const base64Match = img.match(/src=["']data:image\/(png|jpeg|jpg);base64,([^"']+)["']/);
          if (base64Match) {
            const base64 = base64Match[0];
            const s3Url = await uploadBase64ToS3(base64, 'description');
            html = html.replace(base64, s3Url);
          }
        }
      }
      setEditorUploading(false);
    } catch (error) {
      console.error('Description image upload error:', error);
      setEditorUploadError('Failed to upload description image');
      setEditorUploading(false);
    }
    setDescription(html);
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <ShimmerLoader type="form" />
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ marginTop: '20px' }}>
      <Breadcrumbs
        items={[
          { label: 'Blog Details' },
          { label: 'Blogs', onClick: () => navigate('/blog-details/blogs') },
          { label: id ? (title || 'Blog') : 'Add Blog', active: true }
        ]}
      />
      <Form className="p-3" style={{ background: '#fff', borderRadius: 12, maxWidth: 1100, margin: '0 auto' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        <Row className="mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={e => {
                  setTitle(e.target.value);
                  setFieldErrors((prev: any) => ({ ...prev, title: '' }));
                }}
                placeholder="Enter blog title"
              />
              {fieldErrors.title && <div style={{ color: 'red', fontSize: 13 }}>{fieldErrors.title}</div>}
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Publish Date</Form.Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Form.Control type="date" value={publishDate} onChange={e => setPublishDate(e.target.value)} />
                <Button variant="primary" size="sm" onClick={handlePublishNow}>Publish Now</Button>
              </div>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Category</Form.Label>
              <Select
                value={category}
                onChange={val => setCategory(val)}
                options={categories}
                placeholder="Select category"
                isClearable={false}
                isSearchable={false}
                styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
              />
              {fieldErrors.blogCategory && <div style={{ color: 'red', fontSize: 13 }}>{fieldErrors.blogCategory}</div>}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Blog Image</Form.Label>
              <div style={{ position: 'relative', minHeight: 128 }}>
                <Form.Control type="file" accept="image/*" onChange={handleImageChange} ref={fileInputRef} />
                {imagePreview && (
                  <img src={imagePreview} alt="preview" style={{ width: 120, marginTop: 8, borderRadius: 8, opacity: isImageUploading ? 0.5 : 1 }} />
                )}
                {isImageUploading && (
                  <div style={{
                    position: 'absolute',
                    top: imagePreview ? 8 : 40,
                    left: imagePreview ? 48 : 0,
                    width: imagePreview ? 24 : '100%',
                    height: imagePreview ? 24 : '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2
                  }}>
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
                {imageUploadError && <div style={{ color: 'red', marginTop: 8 }}>{imageUploadError}</div>}
              </div>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6} className="d-flex align-items-end">
            <Form.Group className="me-3">
              <Form.Label>Show Banner on Main Blog Page</Form.Label>
              <Form.Check type="switch" checked={isBannerBlog} onChange={e => setIsBannerBlog(e.target.checked)} label={isBannerBlog ? 'Yes' : 'No'} />
            </Form.Group>
            <Form.Group className="me-3">
              <Form.Label>Feature Blog</Form.Label>
              <Form.Check type="switch" checked={isFeatureBlog} onChange={e => setIsFeatureBlog(e.target.checked)} label={isFeatureBlog ? 'Yes' : 'No'} />
            </Form.Group>
            <Form.Group className="me-3">
              <Form.Label>Must Read Blog</Form.Label>
              <Form.Check type="switch" checked={isMustReadBlog} onChange={e => setIsMustReadBlog(e.target.checked)} label={isMustReadBlog ? 'Yes' : 'No'} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Active</Form.Label>
              <Form.Check type="switch" checked={isActive} onChange={e => setIsActive(e.target.checked)} label={isActive ? 'Yes' : 'No'} />
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex align-items-end">
            <Form.Group>
              <Form.Label>Sort Number</Form.Label>
              <Form.Control type="number" value={sortNumber} onChange={e => setSortNumber(Number(e.target.value))} min={1} />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <SunEditor
                setContents={description}
                onChange={val => handleDescriptionImageUpload(val)}
                setOptions={getEditorOptions()}
                imageUploadHandler={handleEditorImageUpload as any}
              />
              {editorUploading && <div style={{ color: '#007bff', marginTop: 8 }}>Uploading image to S3...</div>}
              {editorUploadError && <div style={{ color: 'red', marginTop: 8 }}>{editorUploadError}</div>}
            </Form.Group>
            {fieldErrors.description && <div style={{ color: 'red', fontSize: 13 }}>{fieldErrors.description}</div>}
          </Col>
        </Row>
        
        <hr />
        <h5>SEO Content</h5>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Meta Title</Form.Label>
              <Form.Control
                type="text"
                value={metaTitle}
                onChange={e => {
                  setMetaTitle(e.target.value);
                  setFieldErrors((prev: any) => ({ ...prev, metaTitle: '' }));
                }}
                placeholder="Enter meta title for SEO"
                maxLength={60}
              />
              {fieldErrors.metaTitle && <div style={{ color: 'red', fontSize: 13 }}>{fieldErrors.metaTitle}</div>}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Meta Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={metaDescription}
                onChange={e => {
                  setMetaDescription(e.target.value);
                  setFieldErrors((prev: any) => ({ ...prev, metaDescription: '' }));
                }}
                placeholder="Enter meta description for SEO"
                maxLength={160}
              />
              {fieldErrors.metaDescription && <div style={{ color: 'red', fontSize: 13 }}>{fieldErrors.metaDescription}</div>}
            </Form.Group>
          </Col>
        </Row>
        
        <hr />
        <h5>Blog Content Sections</h5>
        {contents.map((content, idx) => (
          <div key={idx} style={{ border: '1px solid #eee', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Section Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={content.title}
                    onChange={e => handleContentChange(idx, 'title', e.target.value)}
                    placeholder="Enter section title"
                  />
                </Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Section Description</Form.Label>
                  <SunEditor
                    setContents={content.description}
                    onChange={val => handleSectionDescriptionChange(idx, val)}
                    setOptions={getEditorOptions()}
                    imageUploadHandler={handleEditorImageUpload as any}
                  />
                  {editorUploading && <div style={{ color: '#007bff', marginTop: 8 }}>Uploading image to S3...</div>}
                  {editorUploadError && <div style={{ color: 'red', marginTop: 8 }}>{editorUploadError}</div>}
                </Form.Group>
              </Col>
              <Col md={1} className="d-flex align-items-end">
                {contents.length > 1 && (
                  <Button variant="danger" onClick={() => handleRemoveContent(idx)} style={{ marginTop: 24 }}>Remove</Button>
                )}
              </Col>
            </Row>
          </div>
        ))}
        
        <Button 
          variant="outline-primary" 
          onClick={handleAddContent}
          disabled={!canAddNewSection()}
        >
          Add Section
        </Button>
        
        {isUploadingEditorImages && <div style={{ color: '#007bff', marginBottom: 8 }}>Uploading images in content sections...</div>}
        
        <hr />
        <h5>FAQ Section</h5>
        {faqs.length === 0 ? (
          <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 16, marginBottom: 16, textAlign: 'center', color: '#666' }}>
            <p>No FAQs added yet. Click "Add FAQ" to create your first FAQ.</p>
          </div>
        ) : (
          faqs.map((faq, idx) => (
          <div key={idx} style={{ border: '1px solid #eee', borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Question</Form.Label>
                  <Form.Control
                    type="text"
                    value={faq.question}
                    onChange={e => handleFAQChange(idx, 'question', e.target.value)}
                    placeholder="Enter FAQ question"
                  />
                </Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Answer</Form.Label>
                  <SunEditor
                    setContents={faq.answer}
                    onChange={val => handleFAQImageUpload(idx, val)}
                    setOptions={getEditorOptions()}
                    imageUploadHandler={handleEditorImageUpload as any}
                  />
                  {editorUploading && <div style={{ color: '#007bff', marginTop: 8 }}>Uploading image to S3...</div>}
                  {editorUploadError && <div style={{ color: 'red', marginTop: 8 }}>{editorUploadError}</div>}
                </Form.Group>
              </Col>
              <Col md={1} className="d-flex align-items-end">
                {faqs.length > 1 && (
                  <Button variant="danger" onClick={() => handleRemoveFAQ(idx)} style={{ marginTop: 24 }}>Remove</Button>
                )}
              </Col>
            </Row>
          </div>
          ))
        )}
        
        <Button 
          variant="outline-primary" 
          onClick={handleAddFAQ}
          disabled={!canAddNewFAQ()}
        >
          Add FAQ
        </Button>
        
        {isUploadingEditorImages && <div style={{ color: '#007bff', marginBottom: 8 }}>Uploading images in FAQ sections...</div>}
        <div className="d-flex justify-content-end mt-4">
          <Button variant="secondary" className="me-2" onClick={() => navigate('/blog-details/blogs')}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || !isFormValid() || (id ? !hasChanges : false)}
          >
            {saving ? <Spinner size="sm" /> : (id ? 'Update Blog' : 'Save Blog')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BlogDetails; 